/**
 *  @class
 *  @function CartNotificationPopup
 *
 *  Popup shown after an add to cart that deliberately skipped the cart drawer.
 *
 *  Rendered only by main-collection-product-grid, and only while its
 *  "After adding to cart" setting is "Show popup notification". <product-form>
 *  dispatches `cart:notification` instead of opening the cart drawer when the
 *  quick view was opened from that grid — see product.js renderContents().
 *
 *  Everything visual is driven by the section settings: colours, radius and
 *  width arrive as custom properties on the host, labels and copy as markup,
 *  behaviour as data attributes. Nothing here reaches outside the popup except
 *  the optional "open the cart drawer" action, which reuses the theme's own
 *  OverlayManager so drawer state stays consistent.
 */
if (!customElements.get('cart-notification-popup')) {
  class CartNotificationPopup extends HTMLElement {
    connectedCallback() {
      // `position: fixed` resolves against a transformed ancestor rather than
      // the viewport, and section animations transform their wrappers. Living
      // at <body> level keeps the popup pinned to the viewport and out of any
      // section stacking context. Moving re-runs connectedCallback.
      if (this.parentElement !== document.body) {
        document.querySelectorAll('cart-notification-popup').forEach((el) => {
          if (el !== this) el.remove();
        });
        document.body.appendChild(this);
        return;
      }

      this.messageEl = this.querySelector('.cart-notification-popup__message');
      this.productEl = this.querySelector('.cart-notification-popup__product');
      this.imageEl = this.querySelector('.cart-notification-popup__image');
      this.titleEl = this.querySelector('.cart-notification-popup__product-title');
      this.variantEl = this.querySelector('.cart-notification-popup__product-variant');
      this.priceEl = this.querySelector('.cart-notification-popup__product-price');
      this.progressEl = this.querySelector('.cart-notification-popup__progress');
      this.progressBar = this.querySelector('.cart-notification-popup__progress-bar');

      this.messageTemplate = this.dataset.message || '';
      this.showImage = this.dataset.showImage === 'true';
      this.showDetails = this.dataset.showDetails === 'true';
      this.duration = (parseFloat(this.dataset.duration) || 0) * 1000;
      this.timer = null;
      this.remaining = this.duration;
      this.startedAt = 0;
      // Bumped on every show so the deferred work of a previous show/hide
      // cycle can tell it has been superseded and bow out.
      this.token = 0;

      // A hidden element is out of the accessibility tree, so text written into
      // the popup before it animates in would never be announced. Keep the live
      // region separate and permanently visible to assistive technology.
      this.announcer = document.createElement('p');
      this.announcer.className = 'visually-hidden';
      this.announcer.setAttribute('role', 'status');
      this.announcer.setAttribute('aria-live', 'polite');
      this.after(this.announcer);

      this.onNotification = this.onNotification.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('cart:notification', this.onNotification);
      document.addEventListener('keydown', this.onKeydown);

      this.querySelectorAll('[data-cnp-dismiss]').forEach((el) => {
        el.addEventListener('click', () => this.hide());
      });

      const drawerButton = this.querySelector('[data-cnp-open-cart-drawer]');
      if (drawerButton) {
        drawerButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.hide();
          const cartDrawer = document.getElementById('Cart-Drawer');
          if (!cartDrawer || typeof OverlayManager === 'undefined') return;
          OverlayManager.open(cartDrawer);
          dispatchCustomEvent('cart-drawer:open');
        });
      }

      // Never dismiss out from under someone who is reading or tabbing through.
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (e) => {
        // Moving between the popup's own buttons is not leaving it.
        if (this.contains(e.relatedTarget)) return;
        this.resume();
      });

      // QuickView only switches the drawer into notification mode once this is
      // set, so a failed script load falls back to the default cart drawer.
      this.dataset.ready = 'true';

      if (window.Shopify && window.Shopify.designMode) {
        this.boundSectionSelect = this.onSectionSelect.bind(this);
        document.addEventListener('shopify:section:select', this.boundSectionSelect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:notification', this.onNotification);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.boundSectionSelect) {
        document.removeEventListener('shopify:section:select', this.boundSectionSelect);
      }
      if (this.announcer) this.announcer.remove();
      clearTimeout(this.timer);
    }

    // Preview the popup in the theme editor when the merchant selects the
    // product grid, so colours and copy can be tuned without adding to cart.
    onSectionSelect(evt) {
      if (!evt.detail || evt.detail.sectionId !== this.dataset.sectionId) return;
      let sample = {};
      try {
        sample = JSON.parse(this.dataset.preview || '{}');
      } catch (e) {
        sample = {};
      }
      this.show(sample, true);
    }

    onNotification(evt) {
      this.show((evt.detail && evt.detail.item) || {});
    }

    onKeydown(evt) {
      if (evt.key !== 'Escape' && evt.code !== 'Escape') return;
      // Only consume Escape when nothing modal is open — the theme's own
      // handler closes the top-most drawer first.
      if (typeof OverlayManager !== 'undefined' && OverlayManager.hasOpen()) return;
      if (this.classList.contains('is-visible')) this.hide();
    }

    show(item, preview) {
      this.token++;
      this.render(item || {});

      clearTimeout(this.timer);
      this.timer = null;
      this.stopProgress();
      this.hidden = false;
      // Flush the un-hide so the opacity/transform transition actually runs.
      void this.offsetHeight;
      this.classList.add('is-visible');
      this.announce();

      // The editor preview stays put while settings are being tweaked.
      if (preview || !this.duration) return;
      this.remaining = this.duration;
      this.startTimer();
    }

    announce() {
      if (!this.announcer) return;
      const parts = [this.dataset.heading, this.messageEl && this.messageEl.textContent];
      const text = parts.filter(Boolean).join('. ');
      // Re-assigning identical text is not a change, so a second add of the
      // same product would not be announced. Clear, then write on a delay.
      this.announcer.textContent = '';
      if (!text) return;
      const token = this.token;
      setTimeout(() => {
        if (token !== this.token || this.hidden) return;
        this.announcer.textContent = text;
      }, 50);
    }

    hide() {
      if (this.hidden) return;
      clearTimeout(this.timer);
      this.timer = null;
      this.classList.remove('is-visible');
      this.stopProgress();
      if (this.announcer) this.announcer.textContent = '';

      // A show() that lands before this settles supersedes it — without the
      // token the stale backstop below would hide the new popup.
      const token = this.token;
      const done = () => {
        if (token !== this.token) return;
        this.hidden = true;
      };
      const inner = this.querySelector('.cart-notification-popup__inner');
      if (!inner || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        done();
        return;
      }
      // transitionend can be missed (tab hidden, interrupted transition), so
      // keep a timeout as the backstop.
      inner.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
    }

    render(item) {
      const title = item.product_title || item.title || '';
      const quantity = item.quantity || 1;
      let variant = item.variant_title || '';
      if (variant === 'Default Title') variant = '';

      if (this.messageEl) {
        this.messageEl.textContent = this.messageTemplate
          .replace(/\[product\]/g, title)
          .replace(/\[variant\]/g, variant)
          .replace(/\[quantity\]/g, quantity);
      }

      const hasImage = this.showImage && !!item.image;
      const hasDetails = this.showDetails && !!title;

      if (this.imageEl) {
        this.imageEl.hidden = !hasImage;
        if (hasImage) {
          this.imageEl.src = item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=160';
          this.imageEl.alt = title;
        } else {
          this.imageEl.removeAttribute('src');
        }
      }
      if (this.titleEl) this.titleEl.textContent = hasDetails ? title : '';
      if (this.variantEl) {
        this.variantEl.hidden = !hasDetails || !variant;
        this.variantEl.textContent = variant;
      }
      if (this.priceEl) {
        const price = this.formatPrice(item);
        this.priceEl.hidden = !hasDetails || !price;
        this.priceEl.textContent = price;
      }
      if (this.productEl) this.productEl.hidden = !hasImage && !hasDetails;
    }

    formatPrice(item) {
      const cents = item.final_line_price != null ? item.final_line_price : item.final_price;
      if (cents == null || typeof formatMoney !== 'function') return '';
      const format = window.theme && theme.settings && theme.settings.money_with_currency_format;
      if (!format) return '';
      const quantity = item.quantity || 1;
      const money = formatMoney(cents, format);
      return quantity > 1 ? quantity + ' × ' + money : money;
    }

    startTimer() {
      this.startedAt = Date.now();
      this.timer = setTimeout(() => this.hide(), this.remaining);
      this.runProgress();
    }

    pause() {
      if (this.hidden || !this.duration || !this.timer) return;
      clearTimeout(this.timer);
      this.timer = null;
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
      this.freezeProgress();
    }

    resume() {
      if (this.hidden || !this.duration || this.timer || this.remaining <= 0) return;
      this.startTimer();
    }

    runProgress() {
      if (!this.progressBar || !this.progressEl || this.progressEl.hidden) return;
      this.progressBar.style.transition = 'none';
      this.progressBar.style.transform = 'scaleX(' + this.remaining / this.duration + ')';
      void this.progressBar.offsetHeight;
      this.progressBar.style.transition = 'transform ' + this.remaining + 'ms linear';
      this.progressBar.style.transform = 'scaleX(0)';
    }

    freezeProgress() {
      if (!this.progressBar) return;
      const matrix = getComputedStyle(this.progressBar).transform;
      this.progressBar.style.transition = 'none';
      this.progressBar.style.transform = matrix === 'none' ? 'scaleX(1)' : matrix;
    }

    stopProgress() {
      if (!this.progressBar) return;
      this.progressBar.style.transition = 'none';
      this.progressBar.style.transform = 'scaleX(1)';
    }
  }

  customElements.define('cart-notification-popup', CartNotificationPopup);
}
